import request from '@/utils/request';
import type { ImitateLoginParam, LoginResult, TenantUser } from './model';
import type { R, RL, RP } from '@/api';
import { RParam } from '@/api';
import LzdAjax from '@/utils/lzd-ajax';
import { EleMessage } from 'ele-admin-plus';
import LzdUtil, { getDataScopeId, getRoleCode } from '@/utils/lzd-util';

const BASE_URL = '/bsl/tenant/user';

/**
 * 模拟登录
 */
export async function imitateLogin(data: ImitateLoginParam) {
  const res = await request.post<R<LoginResult>>(
    `${BASE_URL}/imitate-login`,
    data
  );
  if (res.data.code === 0) {
    return res.data.data;
  }
  return Promise.reject(new Error(res.data.message));
}

export async function mockLogin(
  url: string,
  userid: string,
  tenantId?: string,
  fn?: (token: string) => void
) {
  const loading = EleMessage.loading('loading....');
  LzdAjax.post<LoginResult>(BASE_URL + url, { userid, tenantId }, true, true)
    .then((v) => {
      setTimeout(() => {
        loading.close();
        if (typeof fn === 'function') {
          fn(v.data!.token!);
        } else {
          const targetUrl = `/?access_token=${encodeURIComponent(v.data!.token!)}`;
          globalThis.open(targetUrl, '_blank');
        }
      }, 1000);
    })
    .catch(() => loading.close());
}

/**
 * 修改当前登录用户的个人信息
 */
export async function updateUserInfo(data: TenantUser): Promise<TenantUser> {
  const res = await request.post<R<TenantUser>>('/bsl/tenant/user/update', {
    dataScopeId: getDataScopeId(),
    roleCode: getRoleCode(),
    update: RParam.buildEdit(data, (k: string) => k !== 'userid')
  });
  if (res.data.code === 0 && res.data.data) {
    return res.data.data;
  }
  return Promise.reject(new Error(res.data.message));
}

/** 系统管理员模拟登录 */
async function sysManagerMockLogin(userid: string, tenantId: string) {
  mockLogin('/sys-manager/mock/login', userid, tenantId);
}

/** 管理员模拟登录 */
async function managerMockLogin(userid: string) {
  mockLogin('/manager/mock/login', userid);
}

/** 管理员模拟登录 */
async function teacherMockLogin(userid: string) {
  mockLogin('/teacher/mock/login', userid);
}

/** 用户绑定手机号 */
export async function authBindPhone(
  phone: string,
  captchaToken: string,
  captchaCode: string
) {
  const formData = new FormData();
  formData.append('phone', phone);
  formData.append('captchaToken', captchaToken);
  formData.append('captchaCode', captchaCode);
  const res = await request.post<R<any>>(
    '/bsl/tenant/auth/bind/phone',
    formData
  );
  if (res.data.code === 0) {
    return res.data;
  }
  return Promise.reject(res.data);
}

/** 用户绑定邮箱 */
export async function authBindEmail(
  email: string,
  captchaToken: string,
  captchaCode: string
) {
  const formData = new FormData();
  formData.append('email', email);
  formData.append('captchaToken', captchaToken);
  formData.append('captchaCode', captchaCode);
  const res = await request.post<R<any>>(
    '/bsl/tenant/auth/bind/email',
    formData
  );
  if (res.data.code === 0) {
    return res.data;
  }
  return Promise.reject(res.data);
}

/** 查询租户应用列表 */
async function queryPage(data: RP) {
  return LzdAjax.post<RL<TenantUser>>(
    '/bsl/tenant/user/manager/query/page',
    data,
    true,
    true
  ).then((v) => v.data);
}

/** 添加管理员 */
async function managerInsert(data: Partial<TenantUser>) {
  return LzdAjax.post(BASE_URL + '/manager/insert', data, true);
}

/** 编辑管理员 */
async function managerUpdate(data: TenantUser) {
  if (!data) {
    LzdUtil.tipsParameterError();
    return;
  }
  return LzdAjax.post<unknown>(
    BASE_URL + '/manager/update',
    {
      tenantId: data.tenantId,
      where: RParam.buildWhere({ userid: data.userid }),
      update: RParam.buildEdit(
        data,
        (k: string) => k !== 'seq' && k !== 'userid'
      )
    },
    true,
    false
  );
}

/**
 * 重置管理员密码
 * @param userid
 * @param password 密码
 * @param tenantId
 */
export async function resetManagerPassword(
  userid: string,
  password: string,
  tenantId: string
) {
  return LzdAjax.post(
    `${BASE_URL}/manager/reset/password`,
    {
      update: RParam.buildEdit({ password }),
      where: [RParam.eq('userid', userid)],
      tenantId: tenantId
    },
    true
  );
}

/**
 * 批量删除管理员
 */
export async function removeManager(
  data: (number | undefined)[],
  tenantId?: string
) {
  return LzdAjax.post(
    `${BASE_URL}/batch/delete`,
    {
      where: RParam.buildWhere({ seq: data }),
      tenantId: tenantId
    },
    true
  );
}

/** 用户API */
class tenantUserApi {
  static sysManagerMockLogin = sysManagerMockLogin;
  static managerMockLogin = managerMockLogin;
  static teacherMockLogin = teacherMockLogin;
  static queryPage = queryPage;
  static managerInsert = managerInsert;
  static managerUpdate = managerUpdate;
  static resetManagerPassword = resetManagerPassword;
  static removeManager = removeManager;
}

export default tenantUserApi;
